import { useEffect, useRef } from 'react';

/**
 * Infinite scroll, handoff rule 3: no load-more buttons anywhere. Attach the
 * returned ref to a sentinel element at the end of the list; `loadMore` fires
 * as it approaches the viewport.
 *
 *   const sentinelRef = useInfiniteScroll(loadMore, { hasMore, loading });
 *   ...
 *   <div ref={sentinelRef} />
 *   {loading ? 'LOADING MORE…' : !hasMore && 'END OF LIST.'}
 */
export default function useInfiniteScroll(loadMore, { hasMore = true, loading = false, rootMargin = '600px' } = {}) {
  const sentinelRef = useRef(null);
  // Keep the latest callback without re-creating the observer on every render.
  const cbRef = useRef(loadMore);
  useEffect(() => { cbRef.current = loadMore; }, [loadMore]);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || loading) return undefined;
    if (typeof IntersectionObserver === 'undefined') return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) cbRef.current?.();
    }, { rootMargin });
    io.observe(node);
    return () => io.disconnect();
  }, [hasMore, loading, rootMargin]);

  return sentinelRef;
}
